import {
  primaryOrderLabel,
  type OpsLookupServiceRow,
  type OpsLookupUnitRow,
} from "@/lib/opsLookup/opsLookupModel";
import { openServiceBoard } from "@/lib/workspaceModeNavigation";

const btnPrimary =
  "rounded-md bg-slate-800 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50 dark:bg-zinc-200 dark:text-zinc-900 dark:hover:bg-white";
const btnGhost =
  "rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-50 dark:border-zinc-600 dark:text-zinc-200 dark:hover:bg-zinc-800";

export function OpsLookupServiceDetail(props: {
  job: OpsLookupServiceRow;
  linkedUnit: OpsLookupUnitRow | null;
  onOpenOrder: (unitId: string) => void;
  onOpenSale: (dealId: string) => void;
}) {
  const { job, linkedUnit, onOpenOrder, onOpenSale } = props;
  const serial = linkedUnit ? primaryOrderLabel(linkedUnit) : "—";
  const quote =
    job.quoted_amount_cents == null
      ? "—"
      : `$${(job.quoted_amount_cents / 100).toLocaleString(undefined, {
          maximumFractionDigits: 2,
        })}`;

  return (
    <section className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-zinc-700">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="text-base font-semibold text-gray-900 dark:text-zinc-100">{job.title}</h3>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700 dark:bg-zinc-800 dark:text-zinc-200">
          {job.kind} · {job.status}
        </span>
      </div>
      <dl className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <dt className="text-gray-500 dark:text-zinc-400">Building serial</dt>
          <dd className="font-medium text-gray-900 dark:text-zinc-100">{serial}</dd>
        </div>
        <div>
          <dt className="text-gray-500 dark:text-zinc-400">Quoted</dt>
          <dd className="font-medium text-gray-900 dark:text-zinc-100">{quote}</dd>
        </div>
        <div className="col-span-2">
          <dt className="text-gray-500 dark:text-zinc-400">Job site</dt>
          <dd className="font-medium text-gray-900 dark:text-zinc-100">{job.site}</dd>
        </div>
        <div>
          <dt className="text-gray-500 dark:text-zinc-400">Scheduled</dt>
          <dd className="font-medium text-gray-900 dark:text-zinc-100">
            {job.scheduled_date ?? "—"}
          </dd>
        </div>
      </dl>
      {job.description.trim() ? (
        <p className="text-sm text-gray-700 dark:text-zinc-300 whitespace-pre-wrap">{job.description}</p>
      ) : null}
      <p className="text-xs text-gray-500 dark:text-zinc-400">
        Read-only here. Open Service Board to schedule or update the job.
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={btnPrimary}
          disabled={!linkedUnit}
          onClick={() => linkedUnit && onOpenOrder(linkedUnit.id)}
        >
          Open unit
        </button>
        <button
          type="button"
          className={btnGhost}
          disabled={!linkedUnit?.ops_deal_id}
          onClick={() => linkedUnit?.ops_deal_id && onOpenSale(linkedUnit.ops_deal_id)}
        >
          Open sale
        </button>
        <button type="button" className={btnGhost} onClick={() => openServiceBoard()}>
          Open Service Board
        </button>
      </div>
    </section>
  );
}

export function OpsLookupRelatedServices(props: {
  jobs: OpsLookupServiceRow[];
  onOpen: (id: string) => void;
}) {
  if (!props.jobs.length) return null;
  return (
    <div className="space-y-1">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Related service</h4>
      <ul className="space-y-1">
        {props.jobs.slice(0, 5).map((j) => (
          <li key={`${j.kind}:${j.id}`}>
            <button
              type="button"
              className="text-left text-sm text-slate-700 underline-offset-2 hover:underline dark:text-zinc-300"
              onClick={() => props.onOpen(j.id)}
            >
              {j.title} · {j.status}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
